import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../store";
import { IFeature, IVersion } from "src/pages/AdminCenter/models/interfaces";
import { IFeatureVersions } from "src/pages/NewAskKural/models/interfaces";
import { setAskKuralVersion } from "src/pages/NewAskKural/states/askKuralSlice";

class FeatureManager {
	private features: IFeature[] = [];
	private versionMap: IFeatureVersions = {};

	constructor(features: IFeature[]) {
		this.features = features;
		this.initializeVersionMap();
	}

	private compareVersions(v1: string, v2: string): number {
		// Remove 'v' prefix if exists and split version numbers
		const v1Parts = v1.replace("v", "").split(".").map(Number);
		const v2Parts = v2.replace("v", "").split(".").map(Number);

		// Compare each part of the version
		for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
			const part1 = v1Parts[i] || 0;
			const part2 = v2Parts[i] || 0;

			if (part1 > part2) return 1;
			if (part1 < part2) return -1;
		}
		return 0;
	}

	private getLatestEnabledVersion(versions: IVersion[]): IVersion | null {
		const enabledVersions = versions.filter((v) => v.enabled);

		if (enabledVersions.length === 0) return null;

		return enabledVersions.reduce((latest, current) => {
			if (!latest) return current;
			return this.compareVersions(current.version_label, latest.version_label) > 0 ? current : latest;
		}, enabledVersions[0]);
	}

	private initializeVersionMap() {
		this.features.forEach((feature) => {
			const latestVersion = this.getLatestEnabledVersion(feature.version_list);
			if (latestVersion) {
				this.versionMap[feature.feature_key] = latestVersion.version_label;
			}
		});
	}

	isFeatureEnabled(featureKey: string): boolean {
		const feature = this.features.find((f) => f.feature_key === featureKey);
		return !!feature?.version_list.some((v) => v.enabled);
	}

	getFeatureVersion(featureKey: string): string | null {
		return this.versionMap[featureKey] || null;
	}

	getAllVersions(): IFeatureVersions {
		return { ...this.versionMap };
	}

	isFeatureBeta(featureKey: string): boolean {
		const feature = this.features.find((f) => f.feature_key === featureKey);
		if (!feature) return false;

		const enabledVersion = feature.version_list.find((v) => v.enabled);
		return enabledVersion?.is_beta === true;
	}

	// Method to get all enabled versions for a feature
	getFeatureEnabledVersions(featureKey: string): string[] {
		const feature = this.features.find((f) => f.feature_key === featureKey);
		if (!feature) return [];

		return feature.version_list
			.filter((v) => v.enabled)
			.map((v) => v.version_label)
			.sort((a, b) => this.compareVersions(b, a)); // Sort in descending order
	}
}

export const getFeatureVersionFromFeatures = (features: IFeature[] | null | undefined, featureKey: string): string | null => {
	if (!features || features.length === 0) return null;
	const featureManager = new FeatureManager(features);
	return featureManager.getFeatureVersion(featureKey);
};

export const useFeatures = () => {
	const dispatch = useDispatch();
	const activeFeatures = useSelector((state: RootState) => state.askKuralSlice.activeFeatures);

	const initializeFeatures = (featuresOverride?: IFeature[]) => {
		const featuresToInitialize = featuresOverride ?? activeFeatures;
		// Only initialize if activeFeatures exists and has items
		if (featuresToInitialize && featuresToInitialize.length > 0) {
			const askKuralVersion = getFeatureVersionFromFeatures(featuresToInitialize, "ask-kural");

			if (askKuralVersion) {
				dispatch(
					setAskKuralVersion({
						configType: "ask-kural",
						version: askKuralVersion,
					})
				);
			}
		}
	};

	const featureManager = activeFeatures?.length > 0 ? new FeatureManager(activeFeatures) : null;

	return {
		isFeatureAvailable: (featureKey: string): boolean => (featureManager ? featureManager.isFeatureEnabled(featureKey) : false),
		getFeatureVersion: (featureKey: string): string | null => (featureManager ? featureManager.getFeatureVersion(featureKey) : null),
		isFeatureBeta: (featureKey: string): boolean => (featureManager ? featureManager.isFeatureBeta(featureKey) : false),
		getAllVersions: (): IFeatureVersions => (featureManager ? featureManager.getAllVersions() : {}),
		getFeatureEnabledVersions: (featureKey: string): string[] => (featureManager ? featureManager.getFeatureEnabledVersions(featureKey) : []),
		initializeFeatures,
	};
};
